/**
 * §9's one focus model, as a decision table.
 *
 * Same reasoning as `playback/input.ts`: a decision table in an event handler is a decision table
 * nobody tests, and this one has to be identical on a TV, where it is not a keyboard at all but a
 * D-pad. Roving `tabindex` (§9) means exactly one element in a group is tabbable and the arrow keys
 * move which one — so the group's whole behaviour is "given a key and where I am, where do I go",
 * which is a pure function of four numbers.
 *
 * `null` means **not ours**: the caller must not `preventDefault`, so an unhandled `↓` still scrolls
 * the page and an `→` at the end of a row still lets a TV's focus engine leave the group. Returning
 * the current index instead would swallow the key and make the app feel stuck — the same distinction
 * `keyToCommand` draws for the player.
 */

export type FocusOrientation = 'horizontal' | 'vertical' | 'grid';

export interface FocusGroup {
  readonly count: number;
  /** Where focus is now. Out-of-range is treated as "nowhere", and any arrow key enters the group. */
  readonly index: number;
  readonly orientation: FocusOrientation;
  /** Grid only. Ignored otherwise; `< 1` degrades to a single column. */
  readonly columns?: number;
  /**
   * Wrap at the ends, for a group that is a closed loop (a sort toggle, a small menu). Off for
   * anything a reader scrolls through: wrapping a 200-row feed from the bottom back to the top loses
   * their place, and on a TV it makes "down" mean "up".
   */
  readonly wrap?: boolean;
}

/** The keys this table claims. Anything else is `null`, including every modifier combination. */
const HORIZONTAL = new Set(['ArrowLeft', 'ArrowRight']);
const VERTICAL = new Set(['ArrowUp', 'ArrowDown']);

function clampIntoGroup(index: number, count: number, wrap: boolean): number | null {
  if (index >= 0 && index < count) return index;
  if (!wrap) return null;
  // A single modulo would turn -1 into -1 in JavaScript.
  return ((index % count) + count) % count;
}

export function focusMoveFor(key: string, group: FocusGroup): number | null {
  const count = group.count;
  if (count <= 0) return null;

  const wrap = group.wrap === true;
  const at = group.index >= 0 && group.index < count ? group.index : null;

  // Home/End are absolute in every orientation — including a grid, where they mean the first and
  // last *item*, not the ends of the current row. That matches every list widget on both platforms.
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;

  const isHorizontal = HORIZONTAL.has(key);
  const isVertical = VERTICAL.has(key);
  if (!isHorizontal && !isVertical) return null;

  if (group.orientation !== 'grid') {
    const claims = group.orientation === 'horizontal' ? isHorizontal : isVertical;
    // The other axis belongs to the page. §7 relies on this: the player scopes `↑/↓` to volume, so a
    // horizontal control inside it must not also answer them.
    if (!claims) return null;
    // Entering a group that has no focus yet: the first key lands on the first item, whichever
    // direction it was.
    if (at === null) return 0;
    const step = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;
    return clampIntoGroup(at + step, count, wrap);
  }

  const columns = Math.max(1, group.columns ?? 1);
  if (at === null) return 0;

  if (isHorizontal) {
    const step = key === 'ArrowRight' ? 1 : -1;
    const row = Math.floor(at / columns);
    const next = at + step;
    // Clamped to the row, deliberately: `→` must never move you *down*. A feed where the last item
    // of a row jumps to the first of the next reads as a wrap on a TV, where `→` is a direction on
    // a screen rather than a position in a list.
    if (next < row * columns || next >= (row + 1) * columns) return null;
    return next < count ? next : null;
  }

  const step = key === 'ArrowDown' ? columns : -columns;
  const next = at + step;
  if (next < 0) return null;
  if (next < count) return next;
  // Down from a column the last row is too short to have. The last row must still be reachable —
  // this is the bug where pressing `↓` on the rightmost thumbnail of the second-to-last row does
  // nothing at all, and it only exists when the item count is not a multiple of the column count.
  const lastRowStart = Math.floor((count - 1) / columns) * columns;
  return at < lastRowStart ? count - 1 : null;
}

/**
 * The `tabIndex` for one member of a roving group — the other half of §9's model, and one line so
 * that no screen has to remember which way round it goes.
 */
export function rovingTabIndex(index: number, focusedIndex: number): 0 | -1 {
  return index === focusedIndex ? 0 : -1;
}
